import { useEffect, useState } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';

type Vote = 'up' | 'down' | null;

/** "Was dit nuttig?" met zichtbare tellers. Wie al gestemd heeft ziet zijn
 *  eigen duim opgelicht — die staat in een ondertekende cookie, dus nog eens
 *  klikken draait de stem om of trekt hem in in plaats van de teller op te
 *  blazen. POST naar /api/stats/feedback. */
export function FeedbackFooter({
  articleId, up = 0, down = 0, mine = null,
}: { articleId: string; up?: number; down?: number; mine?: Vote }) {
  const [counts, setCounts] = useState({ up, down });
  const [vote, setVote] = useState<Vote>(mine);
  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  // Verse cijfers uit de loader: een ander artikel, of een revalidatie.
  useEffect(() => {
    setCounts({ up, down });
    setVote(mine);
    setStatus('idle');
  }, [articleId, up, down, mine]);

  const send = async (kind: 'up' | 'down') => {
    const prev = vote;
    const next: Vote = prev === kind ? null : kind; // opnieuw klikken = intrekken
    const before = counts;

    setCounts({
      up: before.up + (next === 'up' ? 1 : 0) - (prev === 'up' ? 1 : 0),
      down: before.down + (next === 'down' ? 1 : 0) - (prev === 'down' ? 1 : 0),
    });
    setVote(next);
    setStatus('idle');

    try {
      const res = await fetch('/api/stats/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: articleId, articleId, vote: next }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      if (data?.feedback) setCounts({ up: data.feedback.up || 0, down: data.feedback.down || 0 });
      setStatus('saved');
    } catch {
      setCounts(before);
      setVote(prev);
      setStatus('error');
    }
  };

  const message =
    status === 'error' ? 'Versturen mislukt — probeer het later opnieuw.'
    : status === 'saved' ? (vote ? 'Bedankt voor je feedback!' : 'Je stem is ingetrokken.')
    : '';

  return (
    // Een groep met een naam: los voorgelezen zijn "Ja" en "Nee" betekenisloos,
    // want de vraag staat in een span die er niet aan gekoppeld is.
    <div
      role="group"
      aria-labelledby="kb-feedback-vraag"
      className="kb-no-print mt-10 flex flex-wrap items-center gap-3 rounded-lg border border-hairline bg-surface px-4 py-3"
    >
      <span id="kb-feedback-vraag" className="text-base font-semibold text-ink-2">Was dit nuttig?</span>

      <div className="flex gap-2">
        <VoteButton
          kind="up" count={counts.up} active={vote === 'up'} onClick={() => send('up')}
          label="Ja" icon={<ThumbsUp size={15} aria-hidden />}
          aria-label={
            `Ja, dit artikel was nuttig — ${stemmen(counts.up)}` +
            (vote === 'up' ? ', jouw stem. Nogmaals kiezen trekt hem in.' : '')
          }
        />
        <VoteButton
          kind="down" count={counts.down} active={vote === 'down'} onClick={() => send('down')}
          label="Nee" icon={<ThumbsDown size={15} aria-hidden />}
          aria-label={
            `Nee, dit artikel was niet nuttig — ${stemmen(counts.down)}` +
            (vote === 'down' ? ', jouw stem. Nogmaals kiezen trekt hem in.' : '')
          }
        />
      </div>

      {/* Altijd in de DOM: een live region die pas bij het antwoord verschijnt,
          wordt door een deel van de schermlezers niet voorgelezen. */}
      <span role="status" className={`text-base ${status === 'error' ? 'text-danger' : 'text-success'}`}>
        {message}
      </span>
    </div>
  );
}

const stemmen = (n: number) => `${n} ${n === 1 ? 'stem' : 'stemmen'}`;

function VoteButton({
  kind, count, active, label, icon, onClick, ...rest
}: {
  kind: 'up' | 'down';
  count: number;
  active: boolean;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  'aria-label': string;
}) {
  const tone = kind === 'up'
    ? (active
        ? 'border-success bg-success-soft text-success'
        : 'border-hairline-strong text-ink-2 hover:border-success hover:text-success')
    : (active
        ? 'border-danger bg-danger-soft text-danger'
        : 'border-hairline-strong text-ink-2 hover:border-danger hover:text-danger');

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-base font-medium transition ${tone}`}
      {...rest}
    >
      {icon}
      {label}
      <span className="tabular-nums" aria-hidden>{count}</span>
    </button>
  );
}
